import {
  ChatBubbleOutline as MessageCircleIcon,
  Send as SendIcon,
  PersonAdd as UserPlusIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import { TEMPLATE_META, TRIGGER_CHANNELS } from '../constants';
import { getLeadStageLabel } from '../validation/templateValidation';

export function sortTemplates(list) {
  const order = ['comment_reply', 'comment_dm', 'dm_reply', 'custom'];
  return [...(list || [])].sort((a, b) => {
    const aDef = (a.isSystemDefault || a.isChannelDefault) ? 0 : 1;
    const bDef = (b.isSystemDefault || b.isChannelDefault) ? 0 : 1;
    if (aDef !== bDef) return aDef - bDef;
    const ai = order.indexOf(a.templateType);
    const bi = order.indexOf(b.templateType);
    if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  });
}

export function apiTemplateToCard(tpl, settings = null) {
  const meta = TEMPLATE_META[tpl.templateType] || {};
  const TriggerIcon = meta.triggerIcon || MessageCircleIcon;
  const isCommentReply = tpl.templateType === 'comment_reply';
  const isCustom = tpl.templateType === 'custom';
  const triggerChannel = tpl.triggerChannel || tpl.trigger_channel;
  const isCommentChannel = isCommentReply || (isCustom && triggerChannel === 'instagram_comment');
  const firstLine = (tpl.body || '').split('\n')[0] || '';

  let createLead = tpl.createLead ?? tpl.create_lead;
  if (createLead === undefined || createLead === null) {
    if (tpl.templateType === 'comment_reply') createLead = !!settings?.createLeadOnComment;
    else if (tpl.templateType === 'dm_reply') createLead = !!settings?.createLeadOnDm;
    else if (tpl.templateType === 'comment_dm') createLead = false;
    else createLead = true;
  }

  return {
    id: tpl.id,
    templateType: tpl.templateType,
    name: tpl.name || tpl.label,
    status: tpl.status,
    trigger: isCustom
      ? (TRIGGER_CHANNELS.find((c) => c.value === triggerChannel)?.label || meta.trigger)
      : (meta.trigger || tpl.templateType),
    triggerIcon: TriggerIcon,
    flowHint: isCustom
      ? `Keywords: ${(tpl.keywords || []).slice(0, 3).join(', ') || '(none)'}`
      : meta.flowHint,
    body: tpl.body || '',
    dmBody: tpl.dmBody || tpl.dm_body || '',
    keywords: tpl.keywords || [],
    matchRule: tpl.matchRule || tpl.match_rule || 'contains_any',
    triggerChannel,
    createLead,
    isSystemDefault: tpl.isSystemDefault ?? tpl.is_system_default ?? false,
    isChannelDefault: tpl.isChannelDefault ?? tpl.is_channel_default ?? false,
    enableVaanikAgent: tpl.enableVaanikAgent ?? tpl.enable_vaanik_agent ?? false,
    showAssignAgent: tpl.showAssignAgent ?? tpl.show_assign_agent ?? true,
    sortOrder: tpl.sortOrder ?? tpl.sort_order ?? 0,
    replyComment: isCommentChannel ? (tpl.body || '') : '',
    dm: !isCommentChannel ? tpl.body : (tpl.dmBody || tpl.dm_body || ''),
    dmLabel: tpl.name || tpl.label,
    assignAgent: 'Auto',
    triggered: tpl.triggered ?? 0,
    leads: tpl.leads ?? 0,
  };
}

export function buildIntegrationCards(pages) {
  const connected = (pages || []).filter((p) => p.isConnected ?? p.is_connected);
  const igPage = connected.find((p) => p.instagramConnected);
  const fbPage = connected[0];

  return [
    {
      id: 'ig',
      label: 'Instagram',
      icon: InstagramIcon,
      handle: igPage?.instagramUsername ? `@${igPage.instagramUsername}` : 'Not linked',
      connected: Boolean(igPage),
      listens: 'Comments + DMs',
    },
    {
      id: 'fb',
      label: 'Facebook',
      icon: FacebookIcon,
      handle: fbPage?.name || 'No pages connected',
      connected: connected.length > 0,
      listens: 'Comments + Messenger',
    },
  ];
}

export function createEmptyDraft(template) {
  return {
    templateType: template?.templateType || 'custom',
    name: template?.name || '',
    body: template?.body || '',
    dmBody: template?.dmBody || '',
    status: template?.status || 'Active',
    triggerChannel: template?.triggerChannel || 'instagram_comment',
    triggerLibraryId: template?.triggerLibraryId || 'ig-comment',
    keywords: template?.keywords || [],
    matchRule: template?.matchRule || 'contains_any',
    createLead: template?.createLead ?? true,
    assignAgent: template?.assignAgent || 'Auto',
    isChannelDefault: template?.isChannelDefault ?? false,
    enableVaanikAgent: template?.enableVaanikAgent ?? false,
    showAssignAgent: template?.showAssignAgent ?? true,
  };
}

export function createNewAutomationDraft() {
  return createEmptyDraft({
    templateType: 'custom',
    name: '',
    body: 'Thank you for your interest. Please check your DM.',
    dmBody: 'Hi {{name}}\n\nThank you for your interest.',
    status: 'Active',
    triggerChannel: 'instagram_comment',
    triggerLibraryId: 'ig-comment',
    keywords: [],
    matchRule: 'contains_any',
    createLead: true,
    assignAgent: 'Auto',
  });
}

export function getLeadConfigForTemplate(templateType, settings) {
  if (!settings) return { enabled: false, stage: 'new', stageLabel: 'New' };

  if (templateType === 'comment_reply') {
    return {
      enabled: !!settings.createLeadOnComment,
      stage: settings.leadStageOnComment || 'new',
      stageLabel: getLeadStageLabel(settings.leadStageOnComment),
    };
  }

  if (templateType === 'dm_reply') {
    return {
      enabled: !!settings.createLeadOnDm,
      stage: settings.leadStageOnDm || 'new',
      stageLabel: getLeadStageLabel(settings.leadStageOnDm),
    };
  }

  return { enabled: false, stage: 'new', stageLabel: 'New' };
}
